import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calendar, BookOpen, Download, TrendingUp } from "lucide-react";

const fmt = (iso) => {
  const d = new Date(iso);
  // Iteration 86 — force Gregorian calendar. Some mobile browsers (iOS
  // Safari, some Android Chrome builds) default `ar-SA` to the Islamic
  // (Hijri) calendar, which surfaced dates like "26 ربيع الأول 1448 هـ"
  // in Customer > "مكتبتي" on mobile only. Both the locale extension
  // `-u-ca-gregory` and the explicit `calendar: "gregory"` option are set
  // for maximum engine coverage. Display-only — slot_iso is untouched.
  return d.toLocaleString("ar-SA-u-ca-gregory", {
    calendar: "gregory",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const STATUS_LABEL = {
  pending: { ar: "بانتظار الدفع", color: "bg-amber-100 text-amber-800" },
  confirmed: { ar: "مؤكدة", color: "bg-emerald-100 text-emerald-800" },
  canceled: { ar: "ملغاة", color: "bg-rose-100 text-rose-800" },
  completed: { ar: "منتهية", color: "bg-stone-200 text-stone-700" },
};

const BookingRow = ({ b, onCancel, canCancel, onUpgrade }) => {
  const s = STATUS_LABEL[b.status] || STATUS_LABEL.pending;
  return (
    <div className="card-warm p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`booking-row-${b.id}`}>
      <div>
        <div className="font-heading text-xl text-[#222222]">{b.service_title_ar}</div>
        <div className="text-[#5E5C58] text-sm mt-1 flex items-center gap-2">
          <Calendar className="w-4 h-4" /> {fmt(b.slot_iso)}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Badge className={`${s.color} rounded-full`}>{s.ar}</Badge>
        <span className="text-[#0A4D3C] font-heading">
          {Number(b.amount).toLocaleString("ar-SA")} ر.س
        </span>
        {onUpgrade && (
          <Button
            data-testid={`upgrade-booking-btn-${b.id}`}
            onClick={() => onUpgrade(b)}
            variant="outline"
            className="rounded-full border-[#0A4D3C] text-[#0A4D3C] hover:bg-[#0A4D3C] hover:text-white"
          >
            <TrendingUp className="w-4 h-4 ms-1" /> ترقية الجلسة
          </Button>
        )}
      </div>
    </div>
  );
};

const PurchaseRow = ({ p }) => {
  const s = p.status === "paid"
    ? { ar: "مدفوع", color: "bg-emerald-100 text-emerald-800" }
    : p.status === "canceled"
    ? { ar: "ملغي", color: "bg-rose-100 text-rose-800" }
    : { ar: "بانتظار الدفع", color: "bg-amber-100 text-amber-800" };

  const downloadBook = async () => {
    // Iteration 64 — Mobile in-app browser fix.
    // Old approach used fetch() + blob URL + an anchor with `download`. iOS
    // in-app browsers (Gmail, Google App, Instagram, etc.) silently drop
    // both the Authorization header on programmatic clicks AND the
    // `download` attribute, so customers saw nothing happen.
    //
    // New approach: ask the backend for a short-lived signed URL that
    // authorises ?token=... in the query string, then navigate the browser
    // directly to it. The backend serves the PDF inline so iOS opens it in
    // the system PDF viewer (with a built-in share/save sheet). Desktop
    // browsers either show it inline or download it — both are fine.
    try {
      const { data } = await api.post(`/book/download-url/${p.id}`);
      const fullUrl = `${API}${data.url}`;
      // Prefer a new tab so the customer can keep the dashboard open. Most
      // in-app browsers honour _blank when triggered by a real click event.
      const opened = window.open(fullUrl, "_blank", "noopener");
      if (!opened) {
        // Popup blocked → fall back to same-tab navigation.
        window.location.href = fullUrl;
      }
    } catch {
      toast.error(
        "تعذّر تحميل الملف. إذا لم يبدأ التحميل، افتحي الرابط في Safari أو اضغطي مطوّلًا لحفظ الملف."
      );
    }
  };

  return (
    <div className="card-warm p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`purchase-row-${p.id}`}>
      <div>
        <div className="font-heading text-xl text-[#222222] flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#0A4D3C]" /> {p.product_title_ar}
        </div>
        <div className="text-[#5E5C58] text-sm mt-1">{fmt(p.created_at)}</div>
      </div>
      <div className="flex items-center gap-3">
        <Badge className={`${s.color} rounded-full`}>{s.ar}</Badge>
        <span className="text-[#0A4D3C] font-heading">
          {Number(p.amount).toLocaleString("ar-SA")} ر.س
        </span>
        {p.status === "paid" && (
          <Button
            data-testid={`download-book-btn-${p.id}`}
            onClick={downloadBook}
            className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          >
            <Download className="w-4 h-4 ms-1" /> تحميل PDF
          </Button>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState({ upcoming: [], past: [], canceled: [] });
  const [purchases, setPurchases] = useState([]);
  const [services, setServices] = useState([]);
  // Iteration 31 — `?tab=library` (and friends) honoured so the navbar
  // dropdown can deep-link straight into the matching tab.
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return ["upcoming", "past", "canceled", "library"].includes(t) ? t : "upcoming";
  })();
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && ["upcoming", "past", "canceled", "library"].includes(t)) {
      setTab(t);
    }
  }, [searchParams]);

  const refresh = async () => {
    const [b, p, s] = await Promise.all([
      api.get("/dashboard/bookings"),
      api.get("/dashboard/purchases"),
      api.get("/services").catch(() => ({ data: [] })),
    ]);
    setBookings(b.data);
    setPurchases(p.data.items);
    setServices(s.data || []);
  };

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/dashboard");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const cancel = async (b) => {
    if (!window.confirm(`هل تريد إلغاء الحجز "${b.service_title_ar}"؟`)) return;
    try {
      await api.post("/bookings/cancel", { booking_id: b.id });
      toast.success("تم إلغاء الحجز");
      refresh();
    } catch {
      toast.error("تعذّر الإلغاء");
    }
  };

  if (!user) return null;

  // Iteration 88 — "ترقية الجلسة": SOURCE may be a voice OR text (message)
  // session (bundles and books excluded). TARGET is always a strictly more
  // expensive voice session. Server re-validates all of this.
  const upgradeableSources = services.filter(
    (s) => s.type === "consultation" && (s.modality === "voice" || s.modality === "text") && !s.bundle
  );
  const voiceTargets = services.filter(
    (s) => s.type === "consultation" && s.modality === "voice" && !s.bundle
  );
  const canUpgrade = (b) => {
    if (b.status !== "confirmed") return false;
    if (new Date(b.slot_iso) <= new Date()) return false;
    const src = upgradeableSources.find((v) => v.id === b.service_id);
    if (!src) return false;
    return voiceTargets.some((v) => v.id !== src.id && Number(v.price) > Number(src.price));
  };

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        <h1 className="font-heading text-4xl text-[#222222]">
          أهلًا، {user.name}
        </h1>
        <p className="text-[#5E5C58] mt-2">
          هنا تستطيع متابعة جلساتك ومشترياتك الرقمية.
        </p>

        <Tabs value={tab} onValueChange={setTab} className="mt-8" dir="rtl">
          <TabsList className="bg-[#F6F4EE] rounded-full p-1" data-testid="dashboard-tabs">
            <TabsTrigger value="upcoming" className="rounded-full" data-testid="tab-upcoming">
              القادمة ({bookings.upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="rounded-full" data-testid="tab-past">
              السابقة ({bookings.past.length})
            </TabsTrigger>
            <TabsTrigger value="canceled" className="rounded-full" data-testid="tab-canceled">
              الملغاة ({bookings.canceled.length})
            </TabsTrigger>
            <TabsTrigger value="library" className="rounded-full" data-testid="tab-library">
              مكتبتي ({purchases.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-6 space-y-4">
            {bookings.upcoming.length === 0 ? (
              <p className="text-[#5E5C58]">لا توجد جلسات قادمة.</p>
            ) : (
              bookings.upcoming.map((b) => (
                <BookingRow
                  key={b.id}
                  b={b}
                  onCancel={cancel}
                  canCancel
                  onUpgrade={canUpgrade(b) ? (bb) => navigate(`/upgrade/${bb.id}`) : undefined}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-6 space-y-4">
            {bookings.past.length === 0 ? (
              <p className="text-[#5E5C58]">لا توجد جلسات سابقة.</p>
            ) : (
              bookings.past.map((b) => <BookingRow key={b.id} b={b} canCancel={false} />)
            )}
          </TabsContent>

          <TabsContent value="canceled" className="mt-6 space-y-4">
            {bookings.canceled.length === 0 ? (
              <p className="text-[#5E5C58]">لا توجد جلسات ملغاة.</p>
            ) : (
              bookings.canceled.map((b) => <BookingRow key={b.id} b={b} canCancel={false} />)
            )}
          </TabsContent>

          <TabsContent value="library" className="mt-6 space-y-4">
            {purchases.length === 0 ? (
              <p className="text-[#5E5C58]">لم تقم بشراء أي كتاب بعد.</p>
            ) : (
              purchases.map((p) => <PurchaseRow key={p.id} p={p} />)
            )}
          </TabsContent>
        </Tabs>
      </section>
    </PageShell>
  );
};

export default Dashboard;
